<script>
import * as m from '@app/paraglide/messages.js';
import { auth, functions } from "@app/configs/firebase.js";
import { httpsCallable } from "firebase/functions";
import { replace } from "svelte-spa-router";
import { signOut } from "firebase/auth";
import { environment } from "@app/store/envStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";

const ipcRenderer = window.ipcRenderer;

const INCLUDED = "included";
const NOT_INCLUDED = "not";
const ADDON = "addon";

// Stripe Payment Link URLs: pricing v2 (Starter $35/$350, Pro $75/$750), 7-day trial, card at signup.
// Created by functions/test/stripe_migration/create_v2_payment_links.js.
// The Pro links offer the AD Team plugin as an optional add-on.
const paymentLinks = {
    starter: {
        monthly: "https://buy.stripe.com/28E00jePo1UE7pM5lWfQI09",
        yearly: "https://buy.stripe.com/dRm3cv0Yy7eYcK6eWwfQI0a"
    },
    pro: {
        monthly: "https://buy.stripe.com/5kQ00j8r056QaBYcOofQI0l",
        yearly: "https://buy.stripe.com/dRm00j5eOdDm4dAbKkfQI0m"
    }
};

// Pricing
const pricing = {
    starter: {
        monthly: { amount: 35 },
        yearly: { amount: 350 }
    },
    pro: {
        monthly: { amount: 75 },
        yearly: { amount: 750 }
    }
};

// State
let billingPeriod = $state("monthly"); // "monthly" or "yearly"
let loading = $state(false);

const periodLabel = $derived(
    billingPeriod === "yearly" ? m.signup_period_year() : m.signup_period_month()
);

// Allowances shown in the cards and the compare table
const allowances = $derived(billingPeriod === "yearly"
    ? { starterMinutes: "3,600", proMinutes: "7,200", proCharacters: "6,000,000", speechCharacters: "9,000,000" }
    : { starterMinutes: "300", proMinutes: "600", proCharacters: "500,000", speechCharacters: "750,000" });

// Plan cards, in the same order and with the same copy as the website pricing page
const plans = $derived.by(() => {
    const paidPeriod = billingPeriod === "yearly"
        ? m.signup_price_period_yearly()
        : m.signup_price_period_monthly();

    return [
        {
            id: "free",
            name: m.signup_plan_free(),
            audience: m.signup_plan_free_audience(),
            amount: "$0",
            period: m.signup_price_period_free(),
            buttonLabel: m.signup_free_button(),
            buttonClass: "btn-outline-light",
            onclick: startFree,
            note: m.signup_free_note(),
            includes: null,
            features: [
                m.signup_feature_starter_1(),
                m.signup_feature_free_1(),
                m.signup_feature_free_4(),
                m.signup_feature_free_5(),
                m.signup_feature_starter_7(),
                m.signup_feature_free_6(),
                m.signup_feature_free_7()
            ]
        },
        {
            id: "starter",
            name: m.signup_plan_starter(),
            audience: m.signup_plan_starter_audience(),
            amount: `$${pricing.starter[billingPeriod].amount}`,
            period: paidPeriod,
            buttonLabel: m.signup_start_trial_button(),
            buttonClass: "btn-primary",
            onclick: () => selectPlan("starter"),
            note: m.signup_trial_note(),
            includes: m.signup_plan_starter_includes(),
            features: [
                m.signup_feature_starter_1(),
                m.signup_feature_starter_2({ minutes: allowances.starterMinutes, period: periodLabel }),
                m.signup_feature_starter_3(),
                m.signup_feature_starter_4(),
                m.signup_feature_starter_10(),
                m.signup_feature_free_6(),
                m.signup_feature_starter_11()
            ]
        },
        {
            id: "pro",
            featured: true,
            name: m.signup_plan_pro(),
            audience: m.signup_plan_pro_audience(),
            amount: `$${pricing.pro[billingPeriod].amount}`,
            period: paidPeriod,
            buttonLabel: m.signup_start_trial_button(),
            buttonClass: "btn-primary",
            onclick: () => selectPlan("pro"),
            note: m.signup_trial_note(),
            includes: m.signup_plan_pro_includes(),
            features: [
                m.signup_feature_pro_1(),
                m.signup_feature_pro_2({ minutes: allowances.proMinutes, period: periodLabel }),
                m.signup_feature_pro_3(),
                m.signup_feature_pro_4({ characters: allowances.proCharacters, period: periodLabel }),
                m.signup_feature_pro_15(),
                m.signup_feature_pro_5(),
                m.signup_feature_pro_6(),
                m.signup_feature_pro_7(),
                m.signup_feature_pro_9(),
                m.signup_feature_pro_16()
            ]
        }
    ];
});

const comparePlans = ["free", "starter", "pro"];

// Shorthand for rows that are the same on Starter and Pro
const row = (label, free, starter, pro = starter) => ({ label, free, starter, pro });

const compareSections = $derived.by(() => {
    const minUnit = m.signup_compare_unit_min();
    const charUnit = m.signup_compare_unit_chars();
    const perMinute = m.signup_compare_per_minute();
    const allowance = (amount, unit) => m.signup_compare_allowance({ amount, unit, period: periodLabel });
    const speech = allowance(allowances.speechCharacters, charUnit);

    return [
        {
            title: m.signup_compare_section_who(),
            showHeader: false,
            rows: [
                row(
                    m.signup_compare_section_who(),
                    m.signup_compare_who_free(),
                    m.signup_compare_who_starter(),
                    m.signup_compare_who_pro()
                )
            ]
        },
        {
            title: m.signup_compare_section_ai(),
            rows: [
                row(
                    m.signup_compare_captioning(),
                    NOT_INCLUDED,
                    allowance(allowances.starterMinutes, minUnit),
                    allowance(allowances.proMinutes, minUnit)
                ),
                row(m.signup_compare_translation(), NOT_INCLUDED, NOT_INCLUDED, allowance(allowances.proCharacters, charUnit)),
                row(m.signup_compare_shot_descriptions(), NOT_INCLUDED, NOT_INCLUDED, perMinute),
                row(m.signup_compare_ad_dv(), NOT_INCLUDED, NOT_INCLUDED, perMinute),
                row(m.signup_compare_speech(), NOT_INCLUDED, speech),
                row(m.signup_compare_shot_change(), NOT_INCLUDED, NOT_INCLUDED, INCLUDED),
                row(m.signup_compare_auto_sync(), NOT_INCLUDED, NOT_INCLUDED, INCLUDED),
                row(
                    m.signup_compare_ai_assistant(),
                    m.signup_compare_questions_day({ count: "10" }),
                    m.signup_compare_questions_day({ count: "50" })
                ),
                row(
                    m.signup_compare_ai_agent(),
                    NOT_INCLUDED,
                    NOT_INCLUDED,
                    m.signup_compare_allowance({
                        amount: "500",
                        unit: m.signup_compare_unit_credits(),
                        period: m.signup_period_month()
                    })
                )
            ]
        },
        {
            title: m.signup_compare_section_import(),
            rows: [
                row(m.signup_compare_transcript_import(), INCLUDED, INCLUDED),
                row(m.signup_compare_subtitle_import(), INCLUDED, INCLUDED),
                row(m.signup_compare_playback(), INCLUDED, INCLUDED),
                row(m.signup_compare_players(), INCLUDED, INCLUDED),
                row(m.signup_compare_custom_import(), INCLUDED, INCLUDED)
            ]
        },
        {
            title: m.signup_compare_section_export(),
            rows: [
                row(m.signup_compare_transcript_export(), NOT_INCLUDED, INCLUDED),
                row(m.signup_compare_dialogue_export(), NOT_INCLUDED, INCLUDED),
                row(m.signup_compare_embed(), NOT_INCLUDED, NOT_INCLUDED, INCLUDED),
                row(m.signup_compare_basic_export(), INCLUDED, INCLUDED),
                row(m.signup_compare_advanced_export(), NOT_INCLUDED, INCLUDED),
                row(m.signup_compare_ad_export(), NOT_INCLUDED, ADDON),
                row(m.signup_compare_ead_export(), NOT_INCLUDED, ADDON)
            ]
        },
        {
            title: m.signup_compare_section_additional(),
            rows: [
                row(m.signup_compare_timeline(), INCLUDED, INCLUDED),
                row(m.signup_compare_foot_pedal(), INCLUDED, INCLUDED),
                row(m.signup_compare_desktop(), INCLUDED, INCLUDED),
                row(m.signup_compare_web(), INCLUDED, INCLUDED),
                row(m.signup_compare_multichannel(), INCLUDED, INCLUDED),
                row(m.signup_compare_qc(), INCLUDED, INCLUDED),
                row(m.signup_compare_knp(), NOT_INCLUDED, NOT_INCLUDED, INCLUDED),
                row(m.signup_compare_team(), NOT_INCLUDED, NOT_INCLUDED, INCLUDED),
                row(m.signup_compare_replacement(), INCLUDED, INCLUDED),
                row(
                    m.signup_compare_users(),
                    m.signup_compare_users_starter(),
                    m.signup_compare_users_starter(),
                    m.signup_compare_users_pro()
                )
            ]
        },
        {
            title: m.signup_compare_section_support(),
            rows: [
                row(m.signup_compare_chat(), INCLUDED, INCLUDED),
                row(m.signup_compare_updates(), INCLUDED, INCLUDED),
                row(m.signup_compare_training(), NOT_INCLUDED, NOT_INCLUDED, INCLUDED),
                row(m.signup_compare_priority(), NOT_INCLUDED, NOT_INCLUDED),
                row(m.signup_compare_account_manager(), NOT_INCLUDED, NOT_INCLUDED)
            ]
        }
    ];
});

// Handle plan selection
async function selectPlan(plan) {
    try {
        loading = true;

        // Get user email from Firebase auth
        const user = auth.currentUser;
        if (!user) {
            toast.push(m.signup_login_required_toast(), {
                classes: ["toast-danger"]
            });
            loading = false;
            return;
        }

        const email = user.email;

        // Get the payment link URL
        const paymentLinkUrl = paymentLinks[plan][billingPeriod];

        // Build URL with prefilled email
        const checkoutUrl = `${paymentLinkUrl}?prefilled_email=${encodeURIComponent(email)}`;

        // Handle Electron vs Web
        if (environment.electron) {
            // Show alert for Electron users
            const result = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                titleText: m.signup_swal_title(),
                html: m.signup_swal_message(),
                icon: "info",
                confirmButtonText: m.signup_swal_confirm_button(),
                allowOutsideClick: false,
                allowEscapeKey: false,
            });

            if (result.isConfirmed) {
                // Open URL in external browser
                if (ipcRenderer) {
                    await ipcRenderer.invoke("openExternal", checkoutUrl);
                }

                // Quit the application
                if (ipcRenderer) {
                    await ipcRenderer.invoke("quit");
                }
            }
        } else {
            // For web, redirect to Stripe checkout
            window.location.href = checkoutUrl;
        }
    } catch (error) {
        console.error("Error starting checkout:", error);
        toast.push(m.signup_checkout_failed_toast({ message: error.message }), {
            classes: ["toast-danger"]
        });
        loading = false;
    }
}

// Free plan: no checkout, the server puts the account on the $0 plan.
async function startFree() {
    try {
        loading = true;
        if (!auth.currentUser) {
            toast.push(m.signup_login_required_toast(), { classes: ["toast-danger"] });
            loading = false;
            return;
        }
        await httpsCallable(functions, "v8StartFreePlan")();
        replace("/");
    } catch (error) {
        console.error("Error starting the Free plan:", error);
        toast.push(m.signup_free_failed_toast({ message: error.message }), { classes: ["toast-danger"] });
        loading = false;
    }
}

// Handle checkout cancel from query params
function handleCheckoutCancel() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("checkout") === "cancel") {
        toast.push(m.signup_checkout_cancelled_toast(), {
            classes: ["toast-info"]
        });
        // Clear the query param
        window.history.replaceState({}, "", window.location.pathname);
    }
}

function compareCellLabel(value) {
    if (value === INCLUDED) {
        return m.signup_compare_included();
    }
    if (value === NOT_INCLUDED) {
        return m.signup_compare_not_included();
    }
    if (value === ADDON) {
        return m.signup_compare_optional_addon();
    }
    return value;
}

// Switch accounts - log out and return to login page
async function switchAccounts() {
    try {
        await signOut(auth);
    } catch (error) {
        console.error("Error signing out:", error);
    } finally {
        window.location.reload();
    }
}

// Run on mount
handleCheckoutCancel();
</script>

{#snippet compareValue(value)}
    {#if value === INCLUDED}
        <i class="bi bi-check-lg check-icon" aria-hidden="true"></i>
        <span class="visually-hidden">{compareCellLabel(value)}</span>
    {:else if value === NOT_INCLUDED}
        <i class="bi bi-x-lg not-included-icon" aria-hidden="true"></i>
        <span class="visually-hidden">{compareCellLabel(value)}</span>
    {:else if value === ADDON}
        <i class="bi bi-plus-circle addon-icon me-1" aria-hidden="true"></i>
        {compareCellLabel(value)}
    {:else}
        {value}
    {/if}
{/snippet}

<!-- HTML TEMPLATE -->
<div id="SignupScreen">
    <div class="container SignupPageWrapper">
        <div class="row justify-content-center">
            <div class="col-10 SignupFormWrapper">
                <div class="text-center mb-5">
                    <img src="./cc-logo.png" alt={m.app_logo_alt()} class="logo mb-4" />
                    <h1 class="display-5 fw-bold mb-3">{m.signup_title()}</h1>
                    <h2 class="subtitle">{m.signup_subtitle()}</h2>
                    <p class="description">{m.signup_description()}</p>
                </div>

                <!-- Billing Period Toggle -->
                <div class="d-flex justify-content-center mb-5">
                    <div class="btn-group billing-toggle" role="group">
                        <button
                            type="button"
                            class="btn {billingPeriod === 'monthly' ? 'btn-primary' : 'btn-outline-primary'}"
                            onclick={() => billingPeriod = 'monthly'}
                            disabled={loading}>
                            {m.signup_billing_monthly()}
                        </button>
                        <button
                            type="button"
                            class="btn {billingPeriod === 'yearly' ? 'btn-primary' : 'btn-outline-primary'}"
                            onclick={() => billingPeriod = 'yearly'}
                            disabled={loading}>
                            {m.signup_billing_yearly()}
                            <span class="billing-save ms-1">{m.signup_billing_save()}</span>
                        </button>
                    </div>
                </div>

                <!-- Plan Cards: each card is a 4-row subgrid, so the buttons share one row -->
                <div class="plan-grid">
                    {#each plans as plan (plan.id)}
                        <div class="plan-card" class:plan-card-featured={plan.featured}>
                            {#if plan.featured}
                                <span class="plan-flag">{m.signup_most_popular_badge()}</span>
                            {/if}
                            <div class="plan-header">
                                <h3 class="plan-name">{plan.name}</h3>
                                <p class="plan-audience mb-0">{plan.audience}</p>
                            </div>
                            <p class="plan-price">
                                {plan.amount}
                                <span class="plan-period">{plan.period}</span>
                            </p>
                            <div class="plan-cta">
                                <button
                                    class="btn {plan.buttonClass} btn-lg w-100"
                                    onclick={plan.onclick}
                                    disabled={loading}>
                                    {#if loading}
                                        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {/if}
                                    {plan.buttonLabel}
                                </button>
                                <p class="plan-note text-center mt-2 mb-0">{plan.note}</p>
                            </div>
                            <div class="plan-features">
                                {#if plan.includes}
                                    <p class="plan-includes">{plan.includes}</p>
                                {/if}
                                <ul class="plan-checklist">
                                    {#each plan.features as feature (feature)}
                                        <li>
                                            <i class="bi bi-check-lg" aria-hidden="true"></i>
                                            <span>{feature}</span>
                                        </li>
                                    {/each}
                                </ul>
                            </div>
                        </div>
                    {/each}
                </div>
                <div>
                    <p class="footnotes">
                        {@html m.signup_footnote_transcription()}<br>
                        {@html m.signup_footnote_translation()}<br>
                        {@html m.signup_footnote_users()}<br>
                        {@html m.signup_footnote_embedding()}<br>
                        {@html m.signup_footnote_ad()}
                    </p>
                </div>

                <div id="compare-plans" class="compare-plans">
                    <h2 class="compare-title h4 mb-3">{m.signup_compare_title()}</h2>
                    <div class="compare-wrap">
                        <table class="compare-table">
                            <thead>
                                <tr>
                                    <th scope="col"></th>
                                    <th scope="col">{m.signup_plan_free()}</th>
                                    <th scope="col">{m.signup_plan_starter()}</th>
                                    <th scope="col" class="col-featured">{m.signup_plan_pro()}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {#each compareSections as section (section.title)}
                                    {#if section.showHeader !== false}
                                        <tr class="compare-section-row">
                                            <th colspan="4" scope="colgroup">{section.title}</th>
                                        </tr>
                                    {/if}
                                    {#each section.rows as row (row.label)}
                                        <tr class:compare-who-row={section.showHeader === false}>
                                            <th scope="row">{row.label}</th>
                                            {#each comparePlans as planId (planId)}
                                                <td
                                                    class:col-featured={planId === "pro"}
                                                    class:status-not={row[planId] === NOT_INCLUDED}>
                                                    {@render compareValue(row[planId])}
                                                </td>
                                            {/each}
                                        </tr>
                                    {/each}
                                {/each}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div class="text-center help-text">
                    {@html m.signup_help_choosing()}
                </div>

                <div class="text-center help-text fw-bold">
                    <div class="text-center help-text fw-bold s-pQUFMqLZdsz0">Wrong account? <a href="#!/" class="link-accent" onclick="{() => switchAccounts()}">Switch Accounts</a></div>
                </div>
            </div>
        </div>
    </div>
</div>

<style>
    #SignupScreen {
        position: relative;
        min-height: 100vh;
        width: 100vw;
        overflow-x: hidden;
        overflow-y: auto;
        color: #f0f0f0;
        font-family: 'Roboto', sans-serif;
        background: #000000;
        background: linear-gradient(154deg, rgba(0, 0, 0, 1) 0%, rgba(6, 71, 57, 1) 100%);
    }

    .SignupPageWrapper {
        min-height: 100vh;
        padding: 2rem 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    .SignupFormWrapper {
        background: rgba(0, 0, 0, 0.45);
        backdrop-filter: blur(10px);
        border-radius: 15px;
        padding: 40px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .logo {
        max-width: 200px;
        height: auto;
    }

    h1 {
        color: #f0f0f0;
    }

    .subtitle {
        color: rgba(240, 240, 240, 0.8);
        font-size: 2rem;
        font-weight: 400;
    }

    .description {
        color: rgba(240, 240, 240, 0.7);
    }

    /* Billing Toggle */
    .billing-toggle {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        padding: 4px;
    }

    .billing-toggle .btn {
        min-width: 120px;
        border-radius: 6px;
        font-weight: 500;
        transition: all 0.3s ease;
    }

    .billing-toggle .btn-primary {
        background: linear-gradient(135deg, #009978, #00853c);
        border-color: transparent;
    }

    .billing-toggle .btn-outline-primary {
        background: transparent;
        border-color: transparent;
        color: rgba(255, 255, 255, 0.7);
    }

    .billing-toggle .btn-outline-primary:hover {
        background: rgba(255, 255, 255, 0.1);
        color: #f0f0f0;
    }

    .billing-save {
        font-size: 0.75rem;
        font-weight: 700;
        opacity: 0.75;
    }

    /* Plan cards */
    .plan-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        column-gap: 1.5rem;
        row-gap: 2rem;
        padding-top: 0.75rem;
        margin-bottom: 2rem;
    }

    .plan-card {
        position: relative;
        display: grid;
        grid-row: span 4;
        grid-template-rows: subgrid;
        row-gap: 0;
        padding: 2rem;
        background: rgba(0, 0, 0, 0.35);
        backdrop-filter: blur(10px);
        border-radius: 15px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
        transition: transform 0.25s ease, box-shadow 0.25s ease;
    }

    .plan-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 36px rgba(0, 0, 0, 0.35);
    }

    .plan-card-featured {
        background: rgba(0, 0, 0, 0.45);
        border-color: transparent;
        box-shadow: 0 12px 40px rgba(0, 153, 120, 0.18);
    }

    /* Gradient ring on the featured card */
    .plan-card-featured::before {
        content: "";
        position: absolute;
        inset: 0;
        padding: 1.5px;
        border-radius: inherit;
        background: linear-gradient(160deg, #0dfdb5, rgba(0, 153, 120, 0.15) 65%);
        -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
        -webkit-mask-composite: xor;
        mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
        mask-composite: exclude;
        pointer-events: none;
    }

    .plan-flag {
        position: absolute;
        top: 0;
        left: 50%;
        transform: translate(-50%, -50%);
        padding: 0.3rem 0.85rem;
        border: 1px solid rgba(13, 253, 181, 0.45);
        border-radius: 999px;
        background: #04241d;
        color: #0dfdb5;
        font-size: 0.72rem;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        white-space: nowrap;
    }

    .plan-name {
        color: #f0f0f0;
        font-size: 1.4rem;
        font-weight: 700;
        margin-bottom: 0.25rem;
    }

    .plan-audience {
        color: rgba(240, 240, 240, 0.65);
        font-size: 0.95rem;
    }

    .plan-price {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        margin: 1.25rem 0 1.5rem;
        color: #f0f0f0;
        font-size: 2.75rem;
        font-weight: 900;
        letter-spacing: -0.03em;
        line-height: 1;
    }

    .plan-period {
        flex-basis: 100%;
        margin-top: 0.5rem;
        color: rgba(240, 240, 240, 0.6);
        font-size: 0.9rem;
        font-weight: 400;
        letter-spacing: 0;
    }

    .plan-cta {
        margin-bottom: 1.5rem;
    }

    .plan-note {
        color: rgba(255, 255, 255, 0.6);
        font-size: 0.85rem;
    }

    .plan-features {
        align-self: start;
    }

    .plan-includes {
        color: rgba(240, 240, 240, 0.9);
        font-weight: 700;
        font-size: 0.95rem;
        margin-bottom: 0.85rem;
    }

    .plan-checklist {
        display: grid;
        gap: 0.65rem;
        margin: 0;
        padding: 0;
        list-style: none;
        color: rgba(240, 240, 240, 0.85);
        font-size: 0.92rem;
    }

    .plan-checklist li {
        display: flex;
        gap: 0.65rem;
        align-items: flex-start;
        line-height: 1.5;
    }

    .plan-checklist li > .bi {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1.4rem;
        height: 1.4rem;
        border-radius: 50%;
        background-color: rgba(13, 253, 181, 0.12);
        color: #0dfdb5;
        font-size: 0.8rem;
    }

    /* Buttons */
    .btn-primary {
        background: linear-gradient(135deg, #009978, #00853c);
        border: none;
        font-weight: 500;
        border-radius: 8px;
        padding: 12px 20px;
        transition: all 0.3s ease;
    }

    .btn-primary:hover:not(:disabled) {
        background: linear-gradient(135deg, #00b38a, #009948);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(13, 253, 181, 0.3);
    }

    .btn-primary:disabled {
        background: rgba(0, 153, 120, 0.5);
        transform: none;
        box-shadow: none;
    }

    .plan-cta .btn-outline-light {
        border-color: rgba(255, 255, 255, 0.35);
        font-weight: 500;
        border-radius: 8px;
        padding: 12px 20px;
    }

    /* Footnotes */
    .footnotes {
        color: rgba(255, 255, 255, 0.5);
        font-size: 0.8rem;
        line-height: 1.6;
    }

    /* Compare table */
    .compare-plans {
        margin-top: 2.5rem;
    }

    .compare-title {
        color: #f0f0f0;
        font-weight: 600;
        text-align: center;
    }

    .compare-wrap {
        overflow-x: auto;
        border: 1px solid rgba(255, 255, 255, 0.12);
        border-radius: 12px;
        background: rgba(0, 0, 0, 0.2);
    }

    .compare-table {
        width: 100%;
        min-width: 640px;
        border-collapse: collapse;
        color: rgba(240, 240, 240, 0.85);
        font-size: 0.9rem;
    }

    .compare-table th,
    .compare-table td {
        padding: 0.8rem 1.1rem;
        border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        vertical-align: middle;
    }

    .compare-table tbody tr:last-child th,
    .compare-table tbody tr:last-child td {
        border-bottom: none;
    }

    .compare-table thead th {
        color: #f0f0f0;
        font-size: 1rem;
        font-weight: 700;
        text-align: center;
        background: rgba(0, 0, 0, 0.25);
        border-bottom: 1px solid rgba(255, 255, 255, 0.15);
    }

    .compare-table th[scope="row"] {
        width: 34%;
        font-weight: 500;
        color: rgba(240, 240, 240, 0.9);
    }

    .compare-table td {
        width: 22%;
        text-align: center;
        color: rgba(240, 240, 240, 0.85);
    }

    .compare-who-row th,
    .compare-who-row td {
        font-style: italic;
        color: rgba(240, 240, 240, 0.7);
    }

    .compare-section-row th {
        background: rgba(0, 153, 120, 0.18);
        color: #f0f0f0;
        font-weight: 600;
        font-size: 0.8rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    /* Highlighted Pro column */
    .compare-table thead th.col-featured {
        color: #0dfdb5;
        background: rgba(0, 153, 120, 0.22);
    }

    .compare-table td.col-featured {
        background: rgba(0, 153, 120, 0.07);
    }

    .compare-table tbody tr:not(.compare-section-row):hover th,
    .compare-table tbody tr:not(.compare-section-row):hover td {
        background-color: rgba(13, 253, 181, 0.06);
    }

    .check-icon {
        color: #0dfdb5;
        font-size: 1.1rem;
    }

    .not-included-icon {
        color: rgba(255, 255, 255, 0.3);
        font-size: 1rem;
    }

    .addon-icon {
        color: rgba(13, 253, 181, 0.7);
    }

    /* Help Text */
    .help-text {
        color: rgba(255, 255, 255, 0.7);
        margin-top: 1rem;
    }

    /* Loading Spinner */
    .spinner-border-sm {
        width: 1rem;
        height: 1rem;
    }

    /* Custom Scrollbar */
    .SignupFormWrapper::-webkit-scrollbar {
        width: 6px;
    }

    .SignupFormWrapper::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 3px;
    }

    .SignupFormWrapper::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.3);
        border-radius: 3px;
    }

    .SignupFormWrapper::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.5);
    }

    /* Responsive */
    @media (max-width: 991.98px) {
        .plan-grid {
            grid-template-columns: minmax(0, 1fr);
        }

        .plan-card {
            grid-row: auto;
            grid-template-rows: auto;
        }
    }

    @media (max-width: 768px) {
        .SignupFormWrapper {
            padding: 30px 20px;
            margin: 10px;
        }

        .plan-card {
            padding: 1.5rem;
        }

        .plan-price {
            font-size: 2.25rem;
        }

        .compare-table {
            font-size: 0.8rem;
        }
    }
</style>
